import {
	TemplateEditor,
	columnsToStruct,
	configToJson,
	jsonToStruct,
	structToColumns,
	useColumnEditor,
} from "@/components/export-template-editor";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { ExportTemplateService } from "@/gen/omnios/v1/export_template_connect";
import { MediaService } from "@/gen/omnios/v1/media_connect";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/settings/export-templates/$id")({
	component: ExportTemplateDetail,
});

const client = createClient(ExportTemplateService, transport);
const mediaClient = createClient(MediaService, transport);

function getDefaultColumns(t: (key: string) => string) {
	return [
		{ field: "line_number", header: "N", width: 5 },
		{
			field: "product_title",
			header: t("export_templates_detail_col_name"),
			width: 40,
		},
		{
			field: "vendor_code",
			header: t("export_templates_detail_col_vendor_code"),
			width: 15,
		},
		{
			field: "quantity",
			header: t("export_templates_detail_col_quantity"),
			width: 12,
			number_format: "#,##0.00",
		},
		{ field: "unit", header: t("export_templates_detail_col_unit"), width: 8 },
		{
			field: "unit_price",
			header: t("export_templates_detail_col_price"),
			width: 15,
			number_format: "#,##0.00",
		},
		{
			field: "discount_pct",
			header: t("export_templates_detail_col_discount"),
			width: 10,
		},
		{
			field: "line_total",
			header: t("export_templates_detail_col_total"),
			width: 15,
			number_format: "#,##0.00",
		},
	];
}

function ExportTemplateDetail() {
	const { t } = useTranslation("settings");
	const { id } = Route.useParams();
	const navigate = useNavigate();
	const isNew = id === "new";

	// Parse search params for party-scoped creation
	const searchParams = new URLSearchParams(window.location.search);
	const partyId = searchParams.get("partyId") ?? "";
	const scope = partyId ? "party" : "workspace";

	const [loading, setLoading] = useState(!isNew);
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [format, setFormat] = useState("xlsx");
	const [isDefault, setIsDefault] = useState(false);
	const [headerConfig, setHeaderConfig] = useState<Record<string, unknown>>({
		show_date: true,
		show_customer: true,
	});
	const [footerConfig, setFooterConfig] = useState<Record<string, unknown>>({
		show_totals: true,
	});
	const [styling, setStyling] = useState<Record<string, unknown>>({
		alternate_rows: true,
	});
	const [contentFilter, setContentFilter] = useState<{
		category_ids: string[];
		brand_ids: string[];
		product_ids: string[];
		season: string;
		price_min: number;
		price_max: number;
	}>({
		category_ids: [],
		brand_ids: [],
		product_ids: [],
		season: "",
		price_min: 0,
		price_max: 0,
	});
	const [imageNormalize, setImageNormalize] = useState(false);
	const [saving, setSaving] = useState(false);
	const [saved, setSaved] = useState(false);
	const [dirty, setDirty] = useState(false);
	const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const colEditor = useColumnEditor(isNew ? getDefaultColumns(t) : []);

	// XLSX/CSV "load from file" handler. Uploads the file
	// via MediaService, then asks the server to parse the
	// header row and replaces the editor's columns with what
	// it detected. Lets a sales user hand a customer's
	// existing price-list to Omnicom and have the template
	// pre-populated.
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [parsing, setParsing] = useState(false);
	async function handleTemplateFile(file: File) {
		// Guard: replacing existing columns wipes hand-edits.
		// Confirm before overwriting unless the editor is in
		// the default initial state (new template).
		if (!isNew && colEditor.columns.length > 0) {
			const ok = window.confirm(
				`Заменить ${colEditor.columns.length} текущих колонок на колонки из файла? Несохранённые правки будут утеряны.`,
			);
			if (!ok) {
				if (fileInputRef.current) fileInputRef.current.value = "";
				return;
			}
		}
		setParsing(true);
		try {
			const buf = await file.arrayBuffer();
			const upload = await mediaClient.uploadMedia({
				filename: file.name,
				mimeType:
					file.type ||
					(file.name.toLowerCase().endsWith(".csv")
						? "text/csv"
						: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"),
				data: new Uint8Array(buf),
				entityType: "",
				entityId: "",
			});
			const resp = await client.parseTemplateFromMedia({
				mediaId: upload.id,
			});
			if (resp.columns.length === 0) {
				toast.error("Не удалось распознать колонки в файле");
				return;
			}
			colEditor.setColumns(
				resp.columns.map((c) => ({
					field: c.field,
					header: c.header,
					width: c.width || 20,
					number_format: c.numberFormat || undefined,
				})),
			);
			setDirty(true);
			toast.success(
				`Загружено ${resp.columnCount} колонок из ${resp.detectedFormat.toUpperCase()}`,
			);
		} catch (e) {
			toast.error(
				`Ошибка загрузки шаблона: ${e instanceof Error ? e.message : "—"}`,
			);
		} finally {
			setParsing(false);
			if (fileInputRef.current) fileInputRef.current.value = "";
		}
	}

	useBreadcrumbs([
		{ label: t("breadcrumb_settings"), href: "/settings" },
		{
			label: t("export_templates_breadcrumb"),
			href: "/settings/export-templates",
		},
		{ label: isNew ? t("export_templates_new") : name || "..." },
	]);

	// Load existing template
	useEffect(() => {
		if (isNew) return;
		(async () => {
			try {
				const res = await client.getExportTemplate({ id });
				setName(res.name);
				setDescription(res.description);
				setFormat(res.format || "xlsx");
				setIsDefault(res.isDefault);
				colEditor.setColumns(structToColumns(res.columns));
				setHeaderConfig(configToJson(res.headerConfig));
				setFooterConfig(configToJson(res.footerConfig));
				setStyling(configToJson(res.styling));
				const cf = configToJson(res.contentFilter) as Record<string, unknown>;
				setContentFilter({
					category_ids: Array.isArray(cf.category_ids)
						? (cf.category_ids as string[])
						: [],
					brand_ids: Array.isArray(cf.brand_ids)
						? (cf.brand_ids as string[])
						: [],
					product_ids: Array.isArray(cf.product_ids)
						? (cf.product_ids as string[])
						: [],
					season: typeof cf.season === "string" ? cf.season : "",
					price_min: typeof cf.price_min === "number" ? cf.price_min : 0,
					price_max: typeof cf.price_max === "number" ? cf.price_max : 0,
				});
				setImageNormalize(!!res.imageNormalize);
			} catch {
				/* handled by interceptor */
			}
			setLoading(false);
		})();
	}, [id]);

	async function save() {
		setSaving(true);
		setSaved(false);
		try {
			const colsStruct = columnsToStruct(colEditor.columns);
			const cfStruct = jsonToStruct(
				contentFilter as unknown as Record<string, unknown>,
			);
			if (!isNew) {
				await client.updateExportTemplate({
					id,
					name,
					description,
					format,
					isDefault,
					columns: colsStruct,
					headerConfig: jsonToStruct(headerConfig),
					footerConfig: jsonToStruct(footerConfig),
					styling: jsonToStruct(styling),
					contentFilter: cfStruct,
					imageNormalize,
				});
				setDirty(false);
				setSaved(true);
				setTimeout(() => setSaved(false), 2000);
			} else {
				const res = await client.createExportTemplate({
					name,
					description,
					scope,
					partyId,
					format,
					entityType: "order",
					isDefault,
					columns: colsStruct,
					headerConfig: jsonToStruct(headerConfig),
					footerConfig: jsonToStruct(footerConfig),
					styling: jsonToStruct(styling),
					contentFilter: cfStruct,
					imageNormalize,
				});
				// Navigate to edit page for the newly created template
				navigate({
					to: "/settings/export-templates/$id",
					params: { id: res.id },
					replace: true,
				});
			}
		} catch {
			/* handled */
		}
		setSaving(false);
	}

	// Autosave for existing templates (1.5s debounce)
	useEffect(() => {
		if (!dirty || isNew) return;
		if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
		autosaveTimer.current = setTimeout(() => {
			save();
		}, 1500);
		return () => {
			if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
		};
	}, [
		dirty,
		name,
		description,
		format,
		isDefault,
		colEditor.columns,
		headerConfig,
		footerConfig,
		styling,
		contentFilter,
		imageNormalize,
	]);

	function goBack() {
		if (partyId) {
			navigate({ to: "/parties/$id", params: { id: partyId } });
		} else {
			navigate({ to: "/settings/export-templates" });
		}
	}

	if (loading) {
		return (
			<div className="space-y-4 w-full">
				<Skeleton className="h-8 w-64" />
				<Skeleton className="h-96 w-full" />
			</div>
		);
	}

	const markDirty = <T,>(
		setter: React.Dispatch<React.SetStateAction<T>>,
	): React.Dispatch<React.SetStateAction<T>> => {
		return (v: React.SetStateAction<T>) => {
			setter(v);
			setDirty(true);
		};
	};

	return (
		<div className="space-y-4 w-full">
			<Card className="p-3 flex items-center justify-between gap-3">
				<div className="text-sm">
					<div className="font-medium">Загрузить структуру из файла</div>
					<div className="text-xs text-muted-foreground">
						XLSX или CSV — заголовки первой строки станут колонками
						шаблона.
					</div>
				</div>
				<input
					ref={fileInputRef}
					type="file"
					accept=".xlsx,.xls,.csv,.tsv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
					className="hidden"
					onChange={(e) => {
						const f = e.target.files?.[0];
						if (f) handleTemplateFile(f);
					}}
				/>
				<Button
					size="sm"
					variant="outline"
					disabled={parsing}
					onClick={() => fileInputRef.current?.click()}
				>
					{parsing ? (
						<>
							<Loader2 className="h-4 w-4 mr-1 animate-spin" />
							Распознавание…
						</>
					) : (
						<>
							<Upload className="h-4 w-4 mr-1" />
							Загрузить шаблон
						</>
					)}
				</Button>
			</Card>
			<TemplateEditor
				isNew={isNew}
				standalone
				name={name}
				setName={
					isNew
						? setName
						: (v) => {
								setName(v);
								setDirty(true);
							}
				}
				description={description}
				setDescription={
					isNew
						? setDescription
						: (v) => {
								setDescription(v);
								setDirty(true);
							}
				}
				format={format}
				setFormat={
					isNew
						? setFormat
						: (v) => {
								setFormat(v);
								setDirty(true);
							}
				}
				isDefault={isDefault}
				setIsDefault={
					isNew
						? setIsDefault
						: (v) => {
								setIsDefault(v);
								setDirty(true);
							}
				}
				columns={colEditor.columns}
				usedFields={colEditor.usedFields}
				headerConfig={headerConfig}
				setHeaderConfig={isNew ? setHeaderConfig : markDirty(setHeaderConfig)}
				footerConfig={footerConfig}
				setFooterConfig={isNew ? setFooterConfig : markDirty(setFooterConfig)}
				styling={styling}
				setStyling={isNew ? setStyling : markDirty(setStyling)}
				saving={saving}
				saved={saved}
				onAddColumn={
					isNew
						? colEditor.addColumn
						: (k) => {
								colEditor.addColumn(k);
								setDirty(true);
							}
				}
				onRemoveColumn={
					isNew
						? colEditor.removeColumn
						: (i) => {
								colEditor.removeColumn(i);
								setDirty(true);
							}
				}
				onMoveColumn={
					isNew
						? colEditor.moveColumn
						: (i, d) => {
								colEditor.moveColumn(i, d);
								setDirty(true);
							}
				}
				onUpdateColumn={
					isNew
						? colEditor.updateColumn
						: (i, p) => {
								colEditor.updateColumn(i, p);
								setDirty(true);
							}
				}
				onSave={save}
				onCancel={goBack}
			/>
			<ContentFilterPanel
				value={contentFilter}
				onChange={(v) => {
					setContentFilter(v);
					if (!isNew) setDirty(true);
				}}
				imageNormalize={imageNormalize}
				onImageNormalizeChange={(v) => {
					setImageNormalize(v);
					if (!isNew) setDirty(true);
				}}
			/>
		</div>
	);
}

interface ContentFilter {
	category_ids: string[];
	brand_ids: string[];
	product_ids: string[];
	season: string;
	price_min: number;
	price_max: number;
}

function ContentFilterPanel({
	value,
	onChange,
	imageNormalize,
	onImageNormalizeChange,
}: {
	value: ContentFilter;
	onChange: (v: ContentFilter) => void;
	imageNormalize: boolean;
	onImageNormalizeChange: (v: boolean) => void;
}) {
	const { t } = useTranslation("settings");
	const update = <K extends keyof ContentFilter>(
		key: K,
		v: ContentFilter[K],
	) => onChange({ ...value, [key]: v });

	const csvParse = (s: string): string[] =>
		s
			.split(/[,\n]/)
			.map((x) => x.trim())
			.filter(Boolean);

	return (
		<Card className="px-4 py-4 gap-4">
			<div>
				<Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
					{t("export_templates_content_filter", {
						defaultValue: "Содержимое экспорта",
					})}
				</Label>
				<p className="text-xs text-muted-foreground mt-1">
					{t("export_templates_content_filter_hint", {
						defaultValue:
							"Ограничьте, какие товары попадают в экспорт. Пусто = все товары.",
					})}
				</p>
			</div>
			<div className="grid grid-cols-2 gap-3">
				<div className="space-y-1 col-span-2">
					<Label className="text-xs">
						{t("export_templates_brands", { defaultValue: "Бренды" })}
					</Label>
					<Input
						value={value.brand_ids.join(", ")}
						onChange={(e) => update("brand_ids", csvParse(e.target.value))}
						placeholder="Apple, Samsung, Xiaomi"
					/>
				</div>
				<div className="space-y-1 col-span-2">
					<Label className="text-xs">
						{t("export_templates_categories", {
							defaultValue: "ID категорий",
						})}
					</Label>
					<Input
						value={value.category_ids.join(", ")}
						onChange={(e) => update("category_ids", csvParse(e.target.value))}
						placeholder="uuid, uuid, ..."
					/>
				</div>
				<div className="space-y-1 col-span-2">
					<Label className="text-xs">
						{t("export_templates_products", {
							defaultValue: "ID товаров (если выбраны явно)",
						})}
					</Label>
					<Input
						value={value.product_ids.join(", ")}
						onChange={(e) => update("product_ids", csvParse(e.target.value))}
						placeholder="uuid, uuid, ..."
					/>
				</div>
				<div className="space-y-1">
					<Label className="text-xs">
						{t("export_templates_season", { defaultValue: "Сезон" })}
					</Label>
					<Input
						value={value.season}
						onChange={(e) => update("season", e.target.value)}
						placeholder="8 марта, новый год..."
					/>
				</div>
				<div className="space-y-1">
					<Label className="text-xs">
						{t("export_templates_price_min", { defaultValue: "Цена от" })}
					</Label>
					<Input
						type="number"
						min={0}
						value={value.price_min || ""}
						onChange={(e) =>
							update("price_min", Number.parseFloat(e.target.value) || 0)
						}
					/>
				</div>
				<div className="space-y-1">
					<Label className="text-xs">
						{t("export_templates_price_max", { defaultValue: "Цена до" })}
					</Label>
					<Input
						type="number"
						min={0}
						value={value.price_max || ""}
						onChange={(e) =>
							update("price_max", Number.parseFloat(e.target.value) || 0)
						}
						placeholder="0 = без ограничения"
					/>
				</div>
			</div>
			<div className="flex items-center justify-between border-t pt-3">
				<div>
					<Label className="text-sm">
						{t("export_templates_image_normalize", {
							defaultValue: "Белый фон у фото товаров",
						})}
					</Label>
					<p className="text-xs text-muted-foreground mt-0.5">
						{t("export_templates_image_normalize_hint", {
							defaultValue:
								"Автоматически приводить фон изображений к белому при экспорте",
						})}
					</p>
				</div>
				<Switch checked={imageNormalize} onCheckedChange={onImageNormalizeChange} />
			</div>
		</Card>
	);
}
